/**
 * Buttons.
 *
 * `variant` maps to the classes in styles/components/button.css; `primary` is
 * the unadorned base so `<Button>` alone is already the primary action.
 * Loading swaps in a spinner AND disables the control, so a double submit is
 * impossible without every caller remembering to guard it.
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. Use for in-flight mutations. */
  loading?: boolean;
  /** Full-width — the default for the primary action of a narrow form. */
  block?: boolean;
  /** Icon-only: renders a square control. Pass `aria-label` for the name. */
  icon?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, block, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('btn', variant !== 'primary' && variant, size !== 'md' && size, block && 'block', icon && 'icon', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="spinner sm" aria-hidden />}
      {children}
    </button>
  );
});

/** A welded row of mutually exclusive choices (list/grid, table/timeline). */
export function ButtonGroup<T extends string>({ options, value, onChange, ariaLabel }: {
  options: { value: T; label: ReactNode; title?: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="btn-group" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          aria-pressed={value === o.value}
          className={cx(value === o.value && 'active')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A borderless square control for toolbars. `label` becomes the accessible name. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  bordered?: boolean;
  /** Renders `label` as a hover tooltip as well as the accessible name. */
  tip?: boolean;
}>(function IconButton({ label, bordered, tip = true, className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      data-tip={tip ? label : undefined}
      className={cx('icon-btn', bordered && 'bordered', tip && 'has-tip', className)}
      {...rest}
    >
      {children}
    </button>
  );
});
